import { useRender } from "@base-ui/react/use-render";
import type { IconData } from "@loot-os/icons/types";
import type { ReactElement, ReactNode } from "react";
import { Icon } from "../core/Icon.js";
import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { useLinkRender } from "../foundations/linkContext.js";
import { OVER_STAGE } from "../foundations/overStage.js";
import { useLayout } from "../foundations/themeContext.js";

/** The bars' surface: glass floating inset, or solid and edge to edge. */
function useBarClasses(edge: "top" | "bottom", aboveTabs = false) {
  const layout = useLayout();
  const floating = layout.header === "floating";
  return cn(
    "fixed z-40 flex items-center md:hidden",
    layout.chrome === "glass" ? "glass shadow-float" : "bg-bg-canvas",
    floating
      ? cn(
          "inset-x-4 rounded-pill",
          edge === "top"
            ? "top-3"
            : aboveTabs
              ? "bottom-[calc(var(--bottom-bar-inset,0px)+0.75rem)]"
              : "bottom-5",
        )
      : // Anchored to an edge: square, with only the edge that faces the page.
        cn(
          "inset-x-0 rounded-none border-x-0 shadow-none",
          edge === "top"
            ? "top-0 border-t-0 border-b"
            : cn(aboveTabs ? "bottom-(--bottom-bar-inset,0px)" : "bottom-0", "border-t border-b-0"),
          layout.chrome !== "glass" && "border-border-subtle",
        ),
  );
}

export interface MobileTopBarProps {
  /** The mark on Home; Back plus the parent's name on inner pages. */
  start: ReactNode;
  /** Balance and deposit. */
  end?: ReactNode;
  /** The page's stage is under it: glass over it, solid again once it has scrolled past. */
  overStage?: boolean;
}

/** The phone's top bar: where you are on the left, money on the right. No centered title. */
export function MobileTopBar({ start, end, overStage = false }: MobileTopBarProps) {
  return (
    <header
      className={cn(
        useBarClasses("top"),
        "h-14 justify-between gap-3 px-3",
        overStage && OVER_STAGE,
      )}
    >
      {start}
      <div className="flex items-center gap-2">{end}</div>
    </header>
  );
}

export interface MobileTab {
  id: string;
  label: string;
  icon: IconData;
  href?: string;
  current?: boolean;
  render?: ReactElement;
}

function Tab({ label, icon, href, current = false, render }: Omit<MobileTab, "id">) {
  return useRender({
    render: useLinkRender(render),
    defaultTagName: "a",
    props: {
      href,
      "aria-current": current ? "page" : undefined,
      className: cn(
        "flex h-12 flex-1 flex-col items-center justify-center gap-0.5 rounded-pill font-body text-body-b3-strong text-text-tertiary",
        current && "bg-glass-active text-text-primary",
        FOCUS_RING,
      ),
      children: (
        <>
          <Icon icon={icon} size={20} trigger="hover" />
          {label}
        </>
      ),
    },
  });
}

/**
 * A commerce screen's one action on phones (open, buy, confirm), floating where the tab bar
 * would be. At most one brand button in it.
 */
export function MobileActionBar({
  children,
  aboveTabs = false,
}: {
  children: ReactNode;
  /** Rests on the tab bar, for a page that keeps its tabs (the battle room); else at the bottom. */
  aboveTabs?: boolean;
}) {
  return <div className={cn(useBarClasses("bottom", aboveTabs), "p-2")}>{children}</div>;
}

/** The phone's product tabs, at most five. Hidden on full-screen flows (opening, deposit). */
export function MobileTabBar({
  tabs,
  label = "Main",
}: {
  tabs: readonly MobileTab[];
  label?: string;
}) {
  return (
    <nav aria-label={label} className={cn(useBarClasses("bottom"), "h-16 px-2")}>
      <ul className="flex w-full items-center gap-1">
        {tabs.slice(0, 5).map(({ id, ...tab }) => (
          <li key={id} className="flex flex-1">
            <Tab {...tab} />
          </li>
        ))}
      </ul>
    </nav>
  );
}
